import { Search, Visibility } from "@mui/icons-material";
import { Box, Grid, TextField, Typography } from "@mui/material";
import useApi from "_api/index";
import Actions from "components/Car/Actions";
import TableData from "components/Car/Table";
import {
  GradientButton,
  GradientButtonOutline,
} from "components/GradientButton";
import ModalDetailCar from "components/Modal/Car/ModalDetailCar";
import CustomPagination from "components/TableExplanded/Pagination";
import { useEffect, useState } from "react";
import { formatCurrency2Digit, formatDateTime } from "utils/helpers/format";

const RepossessedCars = () => {
  const [dataList, setDataList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState({
    search: "",
  });
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    totalPages: 1,
  });

  const [isOpenModal, setIsOpenModal] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);

  const fetchData = async (isClear = false) => {
    setLoading(true);
    try {
      // Fetch data logic here
      const response = await useApi.pawn_car.getRepossessedCars({
        page: pagination.page,
        limit: pagination.limit,
        ...(!isClear && filter),
      });
      if (response.success) {
        const { data, meta } = response;
        let formattedData = data.map((item) => ({
          // ...item.car,
          ...item.customer,
          ...item,
          car_model: item.model,
        }));
        setDataList(formattedData ?? []);
        setPagination({
          page: meta.page || 1,
          limit: meta.limit || 10,
          total: meta.total || 0,
          totalPages: meta.totalPages || 1,
        });
      }
    } catch (error) {
      console.error("Error fetching repossessed cars:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [pagination.page, pagination.limit]);

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 2,
      }}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 2,
        }}
      >
        <Typography variant="h4">รถหลุดจำนำ</Typography>
      </Box>
      <Grid
        container
        spacing={2}
        alignContent="flex-end"
        justifyContent="flex-end"
      >
        <Grid item xs={12} sm={6} md={4}>
          <Grid container alignItems="center" spacing={1}>
            <Grid item xs={4} textAlign="right">
              <Typography
                sx={{
                  fontSize: "16px",
                  whiteSpace: "nowrap",
                }}
              >
                ค้นหาทั้งหมด
              </Typography>
            </Grid>
            <Grid item xs={8}>
              <TextField
                fullWidth
                variant="outlined"
                placeholder="ค้นหาทั้งหมด"
                sx={{
                  "& .MuiOutlinedInput-root": {
                    bgcolor: "white",
                    height: "40px",
                  },
                }}
                onChange={(e) =>
                  setFilter((prev) => ({ ...prev, search: e.target.value }))
                }
                value={filter.search}
              />
            </Grid>
          </Grid>
        </Grid>
        <Grid item xs={12} sm={6} md={2}>
          <Box
            sx={{
              display: "flex",
              direction: "row",
              alignItems: "center",
              justifyContent: "flex-end",
              gap: 2,
            }}
          >
            <GradientButtonOutline
              sx={{
                px: 3,
                display: "flex",
                alignItems: "center",
                height: "40px",
              }}
              onClick={() => {
                setFilter({ search: "" });
                setPagination((prev) => ({ ...prev, page: 1 }));
                fetchData(true);
              }}
            >
              ล้าง
            </GradientButtonOutline>
            <GradientButton
              sx={{
                px: 3,
                display: "flex",
                alignItems: "center",
                height: "40px",
              }}
              endIcon={<Search />}
              onClick={() => {
                if (pagination.page === 1) {
                  fetchData();
                } else {
                  setPagination((prev) => ({ ...prev, page: 1 }));
                }
              }}
            >
              ค้นหา
            </GradientButton>
          </Box>
        </Grid>
      </Grid>

      <TableData
        loading={loading}
        data={dataList}
        columns={[
          { key: "expand", label: "" },
          {
            key: "picture_cars",
            label: "รูปภาพ",
          },
          { key: "car_code", label: "รหัสรถ" },
          { key: "license_plate", label: "ทะเบียน" },
          { key: "car_brand", label: "ยี่ห้อ" },
          {
            key: "pawn_price",
            label: "ราคาขายฝากรถ",
            render: (row) => formatCurrency2Digit(row.pawn_price),
          },
          {
            key: "pawn_forfeiture_date",
            label: "วันที่หลุดจำนำ",
            render: (row) => {
              return formatDateTime(row.pawn_forfeiture_date);
            },
          },
          {
            key: "actions",
            label: "Actions",
            render: (row) => {
              return (
                <Actions
                  menuList={[
                    {
                      label: "ดูรายละเอียด",
                      icon: <Visibility />,
                      onClick: () => {
                        setSelectedRow(row);
                        setIsOpenModal(true);
                      },
                    },
                  ]}
                />
              );
            },
          },
        ]}
      />

      <CustomPagination
        totalPage={pagination.totalPages}
        page={pagination.page - 1}
        onPageChange={(event, newPage) => {
          setPagination((prev) => ({ ...prev, page: newPage + 1 }));
        }}
        rowsPerPage={pagination.limit}
        onChangeRowsPerPage={(event) => {
          setPagination((prev) => ({
            ...prev,
            limit: event.target.value,
            page: 1,
          }));
        }}
      />

      <ModalDetailCar
        open={isOpenModal}
        onClose={() => setIsOpenModal(false)}
        rowID={selectedRow ? selectedRow.car_id : null}
        type="repossessed_car"
        formlist={[
          {
            id: 1,
            document: "สัญญาจำนำ",
            document_type: "car_pawn_contact",
          },
          {
            id: 2,
            document: "หนังสือมอบอำนาจ",
            document_type: "car_power_of_attorney",
          },
          {
            id: 3,
            document: "แบบคำขอโอนและรับโอน",
            document_type: "car_transform",
          },
        ]}
      />
    </Box>
  );
};

export default RepossessedCars;
